'use client'

import { Button } from '@/components/ui/button'
import { logger } from '@/lib/logger'
import { DateUtil } from '@/lib/utils/date'
import { useTranslations } from 'next-intl'
import { useEffect } from 'react'

/**
 * Error boundary for the product detail route.
 *
 * Purpose: prevent a silent hang if the RSC render throws (e.g., backend
 * timeout / middleware failure). Without this file, a thrown error inside
 * `ProductPage` bubbles up to the locale-level `error.tsx`, and during the
 * bubble the previous page can appear "stuck" behind the top progress bar.
 */
export default function ProductDetailError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const t = useTranslations('global_error')
  const time = new Date().toISOString()
  const currentUrl = (() => {
    if (typeof window === 'undefined') return ''
    try {
      return decodeURI(window.location.href)
    } catch {
      return window.location.href
    }
  })()

  useEffect(() => {
    logger.captureException(error as Error)
  }, [error])

  return (
    <div className='flex min-h-[60vh] w-full items-center justify-center px-4 py-16'>
      <div className='text-center'>
        <h1 className='mb-6 text-2xl font-medium'>{t('error_title')}</h1>

        <div className='flex flex-wrap items-center justify-center gap-3'>
          <Button
            onClick={() => {
              window.location.replace(`/`)
            }}
            variant='white'
          >
            {t('home')}
          </Button>

          <Button
            onClick={() => {
              reset()
            }}
            variant='outline-neutral'
          >
            {t('retry')}
          </Button>
        </div>

        <p className='mt-8 text-xs text-neutral-700'>URL: {currentUrl}</p>
        <p className='text-xs text-neutral-700'>
          Time: {DateUtil.formatFull(time)}
        </p>
      </div>
    </div>
  )
}
